// import { IconButton } from "@package/subframe/components/IconButton"; // TODO: uncomment when date filter is implemented
import { Badge } from "@package/subframe/components/Badge";
import { IconWithBackground } from "@package/subframe/components/IconWithBackground";
import { Loader } from "@package/subframe/components/Loader";
import { Switch } from "@package/subframe/components/Switch";
import { Tabs } from "@package/subframe/components/Tabs";
import {
  FeatherAlertCircle,
  FeatherCheckCircle2,
  FeatherChevronRight,
  // FeatherChevronLeft, // TODO: uncomment when date filter is implemented
  // FeatherChevronRight as FeatherChevronRightNav, // TODO: uncomment when date filter is implemented
  FeatherLoader,
  FeatherWifiOff,
} from "@subframe/core";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";

import { AcknowledgementRequestRow } from "@/components/checklists/AcknowledgementRequestRow";
import { ChecklistRow } from "@/components/checklists/ChecklistRow";
import { ComplianceInstanceRow } from "@/components/checklists/ComplianceInstanceRow";
import { FollowUpActionRow } from "@/components/checklists/FollowUpActionRow";
import { WorkspaceBottomNav } from "@/components/checklists/WorkspaceBottomNav";
import { ReportEventSection } from "@/components/event-records/ReportEventSection";
import { CircularProgress } from "@/components/ui/circular-progress";
import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { useOrganisation } from "@/contexts/OrganisationContext";
import { useListCategoriesQueryControllerList } from "@/generated/categories/categories";
import { useListTodaySessionsQueryControllerList } from "@/generated/log-types/log-types";
import {
  ComplianceTaskInstanceResponseDtoStatus,
  FollowUpActionResponseDtoStatus,
} from "@/generated/model";
import { useFeatureGate } from "@/hooks/use-feature-gate";
import type { ComplianceInstanceListItem } from "@/hooks/use-offline-compliance-instances";
import { useOfflineComplianceInstances } from "@/hooks/use-offline-compliance-instances";
import type { FollowUpActionWithSync } from "@/hooks/use-offline-follow-up-actions";
import { useOfflineFollowUpActions } from "@/hooks/use-offline-follow-up-actions";
import { useOfflineMyAcknowledgements } from "@/hooks/use-offline-my-acknowledgements";
import { prefetchScheduledQuestionnaireDetails } from "@/hooks/use-offline-scheduled-questionnaire-detail";
import {
  type ScheduledQuestionnaireListItemWithSync,
  useOfflineScheduledQuestionnaires,
} from "@/hooks/use-offline-scheduled-questionnaires";
import { partitionAcknowledgements } from "@/lib/acknowledgements/my-acknowledgements";
import { trackChecklistListViewed } from "@/lib/analytics";
import { getDisplayStatus, isCompletedItem } from "@/lib/checklist-utils";
import { isUkToday, isWithinUpcomingDays } from "@/lib/date-utils";
import {
  EMERGENCY_LIGHT_CHECK_COPY,
  EMERGENCY_LIGHT_CHECK_KINDS,
} from "@/lib/emergency-light-checks";
import { FEATURES } from "@/lib/features";
import { countActionableFollowUpActions } from "@/lib/follow-up-actions/actionable-follow-up-actions";
import { isLogSessionSlug } from "@/lib/log-session-slugs";
import { canReportEvents, canSignOffFollowUp } from "@/lib/roles";
import { resolveWorkspaceTab, type WorkspaceTab } from "@/lib/workspace/tabs";

export default function ChecklistsPage() {
  const { user, userRoleType, isPlatformAdmin } = useAuth();
  const { selectedNurseryId, nurseryRole } = useNursery();
  const { isOrgAdmin } = useOrganisation();
  const [hideCompleted, setHideCompleted] = useState(false);
  const navigate = useNavigate();
  const search = useSearch({ from: "/_authenticated/checklists/" });

  const userCanReportEvents = canReportEvents({
    isPlatformAdmin,
    isOrgAdmin,
    nurseryRole,
  });
  const activeTab = resolveWorkspaceTab(search.tab, userCanReportEvents);
  const setActiveTab = (tab: WorkspaceTab) => {
    navigate({
      to: "/checklists",
      search: { tab: tab === "to-do" ? undefined : tab },
      replace: true,
    });
  };

  // Logs data
  const { data: logsResponse, isLoading: logsLoading } =
    useListTodaySessionsQueryControllerList(selectedNurseryId ?? "");
  const logSessions = logsResponse?.data?.filter((s) =>
    isLogSessionSlug(s.logTypeSlug),
  );

  const { items, isLoading, isOffline } =
    useOfflineScheduledQuestionnaires(selectedNurseryId);

  const {
    items: actions,
    isLoading: actionsLoading,
    isOffline: actionsOffline,
  } = useOfflineFollowUpActions(selectedNurseryId, { checklistView: true });

  const { items: complianceInstances, isLoading: complianceLoading } =
    useOfflineComplianceInstances(selectedNurseryId);

  // NAU-410: documents sent to this person to read and sign. Keyed by user as
  // well as nursery — see the hook.
  const { items: acknowledgements, isOffline: acknowledgementsOffline } =
    useOfflineMyAcknowledgements(selectedNurseryId, user?.id ?? null);

  // Category catalog — resolves each compliance instance's primaryCategoryId
  // to its slug/colour for the row's tinted entity icon. Gated like the
  // compliance-instances query itself: on the Launch plan no compliance rows
  // can render, so the catalog would be a wasted request.
  const { hasFeature: hasComplianceTasks } = useFeatureGate(
    FEATURES.COMPLIANCE_TASKS,
  );
  const { data: categoriesResponse } = useListCategoriesQueryControllerList(
    undefined,
    { query: { enabled: hasComplianceTasks } },
  );
  const categories = categoriesResponse?.data.items ?? [];

  // Compute global sync status from all items (checklists + follow-up actions)
  const globalSyncStatus = useMemo(() => {
    let totalPending = 0;
    let totalFailed = 0;
    for (const item of items) {
      if (item.localSyncStatus) {
        totalPending += item.localSyncStatus.pendingCount;
        totalFailed += item.localSyncStatus.failedCount;
      }
    }
    for (const action of actions) {
      if (action.localSyncStatus) {
        totalPending += action.localSyncStatus.pendingCount;
        totalFailed += action.localSyncStatus.failedCount;
      }
    }
    for (const request of acknowledgements) {
      if (request.localSyncStatus) {
        totalPending += request.localSyncStatus.pendingCount;
        totalFailed += request.localSyncStatus.failedCount;
      }
    }
    return { totalPending, totalFailed };
  }, [items, actions, acknowledgements]);

  const {
    todayItems,
    upcomingItems,
    todayActionItems,
    upcomingActionItems,
    todayComplianceItems,
    upcomingComplianceItems,
    todayAcknowledgementItems,
    todayPendingCount,
    todayCompletedCount,
    todayTotalCount,
  } = useMemo(() => {
    let filtered = items;
    if (hideCompleted) {
      filtered = items.filter((item) => !isCompletedItem(item));
    }

    const today: ScheduledQuestionnaireListItemWithSync[] = [];
    const upcoming: ScheduledQuestionnaireListItemWithSync[] = [];
    let todayPending = 0;
    let todayCompleted = 0;
    let todayTotal = 0;

    // Count all today items (without hideCompleted filter) for progress calculation
    items.forEach((item) => {
      const scheduledDate = new Date(item.scheduledDate);
      const status = getDisplayStatus(item);

      if (
        status === "overdue" ||
        status === "in_progress" ||
        status === "due" ||
        isUkToday(scheduledDate)
      ) {
        todayTotal++;
        if (isCompletedItem(item)) {
          todayCompleted++;
        }
      }
    });

    filtered.forEach((item) => {
      const scheduledDate = new Date(item.scheduledDate);
      const status = getDisplayStatus(item);

      // "due" means availableFrom <= now <= availableUntil — the instance is
      // currently inside its active window. For long-windowed cadences (e.g.
      // a monthly checklist available Day 1 / due Day 28) the scheduledDate
      // may be days behind today while the window stays open, so we must
      // surface it in Today rather than fall through to upcoming (NAU-233).
      if (
        status === "overdue" ||
        status === "in_progress" ||
        status === "due"
      ) {
        today.push(item);
        todayPending++;
      } else if (isUkToday(scheduledDate)) {
        today.push(item);
        if (!isCompletedItem(item)) todayPending++;
      } else if (isWithinUpcomingDays(scheduledDate, 7)) {
        upcoming.push(item);
      }
    });

    today.sort((a, b) => {
      const statusA = getDisplayStatus(a);
      const statusB = getDisplayStatus(b);
      const aAvailable = statusA !== "pending";
      const bAvailable = statusB !== "pending";

      // Available (actionable) items before not-yet-available
      if (aAvailable && !bAvailable) return -1;
      if (!aAvailable && bAvailable) return 1;

      if (aAvailable && bAvailable) {
        // Both actionable: sort by deadline (closest first)
        const timeDiff =
          new Date(a.availableUntil).getTime() -
          new Date(b.availableUntil).getTime();
        if (timeDiff !== 0) return timeDiff;
      } else {
        // Both not yet available: sort by availableFrom (soonest first)
        const timeDiff =
          new Date(a.availableFrom).getTime() -
          new Date(b.availableFrom).getTime();
        if (timeDiff !== 0) return timeDiff;
      }

      return a.id.localeCompare(b.id);
    });

    upcoming.sort((a, b) => {
      const timeDiff =
        new Date(a.scheduledDate).getTime() -
        new Date(b.scheduledDate).getTime();
      if (timeDiff !== 0) return timeDiff;
      return a.id.localeCompare(b.id);
    });

    // All undone follow-up actions go into "today" so practitioners can act on them immediately
    const todayActions: FollowUpActionWithSync[] = [...actions].sort((a, b) => {
      const aOverdue = a.status === FollowUpActionResponseDtoStatus.OVERDUE;
      const bOverdue = b.status === FollowUpActionResponseDtoStatus.OVERDUE;
      if (aOverdue && !bOverdue) return -1;
      if (bOverdue && !aOverdue) return 1;
      if (!a.dueDate && b.dueDate) return 1;
      if (a.dueDate && !b.dueDate) return -1;
      if (a.dueDate && b.dueDate) {
        const cmp = (a.dueDate as string).localeCompare(b.dueDate as string);
        if (cmp !== 0) return cmp;
      }
      return a.id.localeCompare(b.id);
    });

    // Partition compliance instances: OVERDUE / DUE_SOON / AWAITING_EVIDENCE
    // go to Today (urgent — practitioner needs to either complete the task or
    // attach the missing certificate). NOT_YET_DUE goes to Upcoming.
    // COMPLETED / CANCELLED are excluded from the "to do" list — they're done.
    const todayCompliance: ComplianceInstanceListItem[] = [];
    const upcomingCompliance: ComplianceInstanceListItem[] = [];
    for (const inst of complianceInstances) {
      if (
        inst.status === ComplianceTaskInstanceResponseDtoStatus.COMPLETED ||
        inst.status === ComplianceTaskInstanceResponseDtoStatus.CANCELLED
      ) {
        continue;
      }
      if (
        inst.status === ComplianceTaskInstanceResponseDtoStatus.OVERDUE ||
        inst.status === ComplianceTaskInstanceResponseDtoStatus.DUE_SOON ||
        inst.status ===
          ComplianceTaskInstanceResponseDtoStatus.AWAITING_EVIDENCE
      ) {
        todayCompliance.push(inst);
        todayPending++;
        todayTotal++;
      } else {
        upcomingCompliance.push(inst);
      }
    }

    // Sort Today compliance: overdue first, then by due date ascending.
    todayCompliance.sort((a, b) => {
      const aOverdue =
        a.status === ComplianceTaskInstanceResponseDtoStatus.OVERDUE;
      const bOverdue =
        b.status === ComplianceTaskInstanceResponseDtoStatus.OVERDUE;
      if (aOverdue && !bOverdue) return -1;
      if (bOverdue && !aOverdue) return 1;
      return a.dueDate.localeCompare(b.dueDate);
    });

    // Sort Upcoming compliance by due date ascending.
    upcomingCompliance.sort((a, b) => a.dueDate.localeCompare(b.dueDate));

    const { today: todayAcknowledgements } = partitionAcknowledgements(
      hideCompleted
        ? acknowledgements.filter((request) => !request.signedAt)
        : acknowledgements,
      new Date(),
    );

    return {
      todayItems: today,
      upcomingItems: upcoming,
      todayActionItems: todayActions,
      upcomingActionItems: [] as FollowUpActionWithSync[],
      todayComplianceItems: todayCompliance,
      upcomingComplianceItems: upcomingCompliance,
      // Everything outstanding is Today: an acknowledgement has no availability
      // window, so there is no "not due yet" state for it (AC10).
      todayAcknowledgementItems: todayAcknowledgements,
      todayPendingCount: todayPending + todayAcknowledgements.length,
      todayCompletedCount: todayCompleted,
      todayTotalCount: todayTotal + todayAcknowledgements.length,
    };
  }, [items, hideCompleted, actions, complianceInstances, acknowledgements]);

  const todayToCompleteCount =
    todayPendingCount +
    countActionableFollowUpActions(
      todayActionItems,
      canSignOffFollowUp({ userRoleType }),
    );

  const prefetchedRef = useRef<Set<string>>(new Set());
  const hasTrackedListView = useRef(false);

  // Track checklist list viewed event (once per page load)
  useEffect(() => {
    if (isLoading || hasTrackedListView.current) return;

    // Count items by status
    let dueTodayCount = 0;
    let overdueCount = 0;
    let upcomingCount = 0;

    items.forEach((item) => {
      const status = getDisplayStatus(item);
      const scheduledDate = new Date(item.scheduledDate);

      if (status === "overdue" || status === "missed") {
        overdueCount++;
      } else if (status === "due" || status === "in_progress") {
        dueTodayCount++;
      } else if (isWithinUpcomingDays(scheduledDate, 7)) {
        upcomingCount++;
      }
    });

    trackChecklistListViewed({
      dueTodayCount,
      overdueCount,
      upcomingCount,
    });
    hasTrackedListView.current = true;
  }, [items, isLoading]);

  useEffect(() => {
    if (isLoading || isOffline || items.length === 0) return;

    const actionableItems = items.filter((item) => {
      const status = getDisplayStatus(item);
      return (
        (status === "due" ||
          status === "overdue" ||
          status === "in_progress") &&
        !prefetchedRef.current.has(item.id)
      );
    });

    if (actionableItems.length === 0) return;

    actionableItems.forEach((item) => prefetchedRef.current.add(item.id));

    const actionableIds = actionableItems.map((item) => item.id);

    prefetchScheduledQuestionnaireDetails(actionableIds).catch((err) => {
      console.error("Background prefetch failed:", err);
    });
  }, [items, isLoading, isOffline]);

  const firstName = user?.firstName || "there";

  return (
    <div className="flex w-full flex-col items-start bg-white animate-fade-in mobile:pb-24 tablet:pb-24">
      {/* Tab Controller — desktop only; on mobile the bottom nav takes over.
          The "logs" tab key stays in the URL for bookmark compat — the
          user-visible label is now "Monitoring" (NAU-316). */}
      <div
        className="flex w-full items-center pb-4 mobile:hidden tablet:hidden"
        data-testid="workspace-tabs"
      >
        <Tabs>
          <Tabs.Item
            active={activeTab === "to-do"}
            onClick={() => setActiveTab("to-do")}
          >
            To do
          </Tabs.Item>
          <Tabs.Item
            active={activeTab === "logs"}
            onClick={() => setActiveTab("logs")}
          >
            Monitoring
          </Tabs.Item>
          {userCanReportEvents && (
            <Tabs.Item
              active={activeTab === "events"}
              onClick={() => setActiveTab("events")}
            >
              Events
            </Tabs.Item>
          )}
        </Tabs>
      </div>
      <WorkspaceBottomNav
        activeTab={activeTab}
        showEvents={userCanReportEvents}
        onTabChange={setActiveTab}
      />

      {activeTab === "logs" ? (
        <LogsTabContent sessions={logSessions} isLoading={logsLoading} />
      ) : activeTab === "events" ? (
        <ReportEventSection />
      ) : (
        <>
          {/* Welcome Section */}
          <div className="flex w-full flex-col items-start gap-4 rounded-md bg-default-background px-0 py-2 mobile:flex-col mobile:flex-nowrap mobile:gap-2 mobile:py-0 mobile:mb-2 mobile:px-0">
            <div className="flex w-full flex-col items-start gap-4 px-0 mobile:px-4 pb-8 mobile:gap-8 mobile:rounded-md mobile:flex-row mobile:bg-alternate-background mobile:px-8 mobile:py-8 mobile:items-center">
              <CircularProgress
                value={
                  todayTotalCount > 0
                    ? (todayCompletedCount / todayTotalCount) * 100
                    : 0
                }
                size={64}
                strokeWidth={7}
              />
              <div className="flex flex-col items-start gap-1">
                <span className="text-heading-2 font-heading-2 text-default-font mobile:text-primary-navy-800">
                  Welcome back,
                  <span className="mobile:hidden">&nbsp;{firstName}</span>
                  <br className="hidden mobile:inline" />
                  <span className="hidden mobile:inline">{firstName}</span>
                </span>
                <span className="text-caption font-caption text-subtext-color mobile:text-body mobile:font-body">
                  You have {todayToCompleteCount} item
                  {todayToCompleteCount !== 1 ? "s" : ""} to complete today
                </span>
              </div>
            </div>

            {/* Offline Banner */}
            {(isOffline || actionsOffline) && (
              <div className="flex w-full items-center gap-3 rounded-sm bg-warning-100 px-4 py-3">
                <IconWithBackground
                  variant="warning"
                  size="medium"
                  icon={<FeatherWifiOff />}
                  square={true}
                />
                <span className="text-body font-body text-warning-800">
                  You&apos;re offline. Showing cached data.
                </span>
              </div>
            )}

            {/* Sync Failed Banner */}
            {globalSyncStatus.totalFailed > 0 && (
              <div className="flex w-full items-center gap-3 rounded-sm bg-error-100 px-4 py-3">
                <IconWithBackground
                  variant="error"
                  size="medium"
                  icon={<FeatherAlertCircle />}
                  square={true}
                />
                <span className="text-body font-body text-error-800">
                  {globalSyncStatus.totalFailed} item
                  {globalSyncStatus.totalFailed !== 1 ? "s" : ""} failed to
                  sync. Check your connection and try again.
                </span>
              </div>
            )}

            {/* Syncing Banner */}
            {globalSyncStatus.totalPending > 0 &&
              globalSyncStatus.totalFailed === 0 &&
              !isOffline && (
                <div className="flex w-full items-center gap-3 rounded-sm bg-neutral-100 px-4 py-3">
                  <IconWithBackground
                    variant="neutral"
                    size="medium"
                    icon={<FeatherLoader className="animate-spin" />}
                    square={true}
                  />
                  <span className="text-body font-body text-neutral-700">
                    Syncing {globalSyncStatus.totalPending} item
                    {globalSyncStatus.totalPending !== 1 ? "s" : ""}...
                  </span>
                </div>
              )}

            {/* Hide Completed Toggle */}

            <div className="flex items-start gap-3 rounded-sm bg-secondary-beige-100 -mx-8 w-[calc(100%+4rem)] mobile:mx-0 mobile:w-full px-4 py-4 mobile:rounded-md mobile:bg-alternate-background">
              <div className="flex grow shrink-0 basis-0 items-center gap-4">
                <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                  <span className="w-full text-body-bold font-body-bold text-default-font mobile:text-large-button mobile:font-large-button">
                    Hide completed
                  </span>
                </div>
                <Switch
                  checked={hideCompleted}
                  onCheckedChange={setHideCompleted}
                />
              </div>
            </div>

            {/* Date Navigation - Mobile Only (TODO: implement date filtering) */}
            {/* <div className="hidden items-center justify-center gap-2 mobile:flex mobile:h-auto mobile:w-full mobile:flex-none mobile:flex-row mobile:flex-nowrap mobile:justify-between mobile:px-0 mobile:pt-2 mobile:pb-0">
          <IconButton
            variant="neutral-primary"
            size="large"
            icon={<FeatherChevronLeft />}
            disabled
          />
          <span className="text-body font-body text-subtext-color mobile:text-default-font">
            Today
          </span>
          <IconButton
            variant="neutral-primary"
            size="large"
            icon={<FeatherChevronRight />}
            disabled
          />
        </div> */}
          </div>

          {/* Checklist Content */}
          <div className="flex w-full flex-col items-start gap-6 pb-8 mobile:px-0 mobile:py-0">
            {/* Date Navigation - Desktop (TODO: implement date filtering) */}
            <div className="flex w-full items-center justify-between pt-2 mobile:hidden">
              <span className="text-heading-2 font-heading-2 text-neutral-900">
                To do today
              </span>
              {/* <div className="flex items-center justify-center gap-2">
            <IconButton icon={<FeatherChevronLeft />} disabled />
            <span className="text-body font-body text-subtext-color">
              Today
            </span>
            <IconButton icon={<FeatherChevronRight />} disabled />
          </div> */}
            </div>

            {isLoading || actionsLoading || complianceLoading ? (
              <div className="flex w-full items-center justify-center py-12">
                <Spinner className="h-8 w-8 text-primary" />
              </div>
            ) : items.length === 0 &&
              actions.length === 0 &&
              complianceInstances.length === 0 &&
              acknowledgements.length === 0 ? (
              <div className="flex w-full flex-col items-center justify-center py-12 gap-4">
                <IconWithBackground
                  variant="neutral"
                  size="large"
                  icon={<FeatherCheckCircle2 />}
                />
                <div className="flex flex-col items-center gap-2">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Nothing to do
                  </span>
                  <span className="text-body font-body text-subtext-color text-center max-w-sm">
                    You don&apos;t have any scheduled checklists, follow-up
                    actions, compliance tasks, or documents to sign right now.
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex w-full flex-col items-start gap-2 mobile:px-0 mobile:py-0">
                <div className="flex w-full flex-col items-start gap-0.5 rounded-sm bg-white">
                  {/* Today Section */}
                  {(todayItems.length > 0 ||
                    todayActionItems.length > 0 ||
                    todayComplianceItems.length > 0 ||
                    todayAcknowledgementItems.length > 0) && (
                    <>
                      <div className="flex w-full flex-col items-start pb-1.5">
                        <div className="flex w-full flex-col items-start mobile:w-[calc(100%+2rem)] mobile:-mx-4 rounded-sm bg-secondary-beige-100 pl-4 pr-2 py-0.5 mobile:rounded-none">
                          <span className="text-caption-bold font-caption-bold text-neutral-500 py-2 mobile:font-['Manrope'] mobile:text-[14px] mobile:font-[700] mobile:leading-[16px] mobile:tracking-tight">
                            Today
                          </span>
                        </div>
                      </div>
                      {/*
                        First in Today, above follow-up actions. This is the only
                        row type that is a legally-permanent read-and-sign
                        obligation with an external deadline, and it is the least
                        discoverable because it is new.
                      */}
                      {todayAcknowledgementItems.map((request, index) => (
                        <div className="w-full" key={request.requestId}>
                          <AcknowledgementRequestRow
                            request={request}
                            isOffline={acknowledgementsOffline}
                          />
                          {/*
                            Each block's last row draws a divider only if a LATER
                            block has rows — so this condition has to name all
                            three of the blocks that follow it.
                          */}
                          {(index < todayAcknowledgementItems.length - 1 ||
                            todayActionItems.length > 0 ||
                            todayComplianceItems.length > 0 ||
                            todayItems.length > 0) && (
                            <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                          )}
                        </div>
                      ))}
                      {todayActionItems.map((action, index) => (
                        <div className="w-full" key={action.id}>
                          <FollowUpActionRow
                            action={action}
                            isOffline={actionsOffline}
                          />
                          {(index < todayActionItems.length - 1 ||
                            todayComplianceItems.length > 0 ||
                            todayItems.length > 0) && (
                            <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                          )}
                        </div>
                      ))}
                      {todayComplianceItems.map((instance, index) => (
                        <div className="w-full" key={instance.id}>
                          <ComplianceInstanceRow
                            instance={instance}
                            categories={categories}
                          />
                          {(index < todayComplianceItems.length - 1 ||
                            todayItems.length > 0) && (
                            <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                          )}
                        </div>
                      ))}
                      {todayItems.map((item, index) => (
                        <div className="w-full" key={item.id}>
                          <ChecklistRow item={item} isOffline={isOffline} />
                          {index < todayItems.length - 1 && (
                            <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                          )}
                        </div>
                      ))}
                    </>
                  )}

                  {/* Upcoming Section */}
                  {(upcomingItems.length > 0 ||
                    upcomingActionItems.length > 0 ||
                    upcomingComplianceItems.length > 0) && (
                    <>
                      <div className="flex w-full flex-col items-start py-1.5">
                        <div className="flex w-full flex-col items-start mobile:w-[calc(100%+2rem)] mobile:-mx-4 rounded-sm bg-secondary-beige-100 pl-4 pr-2 py-0.5 mobile:rounded-none">
                          <span className="text-caption-bold font-caption-bold text-neutral-500 py-2">
                            Upcoming
                          </span>
                        </div>
                      </div>
                      {upcomingActionItems.map((action, index) => (
                        <div className="w-full" key={action.id}>
                          <FollowUpActionRow
                            action={action}
                            isOffline={actionsOffline}
                          />
                          {(index < upcomingActionItems.length - 1 ||
                            upcomingComplianceItems.length > 0 ||
                            upcomingItems.length > 0) && (
                            <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                          )}
                        </div>
                      ))}
                      {upcomingComplianceItems.map((instance, index) => (
                        <div className="w-full" key={instance.id}>
                          <ComplianceInstanceRow
                            instance={instance}
                            categories={categories}
                          />
                          {(index < upcomingComplianceItems.length - 1 ||
                            upcomingItems.length > 0) && (
                            <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                          )}
                        </div>
                      ))}
                      {upcomingItems.map((item, index) => (
                        <div className="w-full" key={item.id}>
                          <ChecklistRow item={item} isOffline={isOffline} />
                          {index < upcomingItems.length - 1 && (
                            <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
                          )}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

type LogSession = {
  logTypeId: string;
  logTypeName: string;
  logTypeDescription: string | null;
  entryCount: number;
};

function LogsTabContent({
  sessions,
  isLoading,
}: {
  sessions: LogSession[] | undefined;
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center py-12">
        <Loader size="large" />
      </div>
    );
  }

  return <LogSessionRows sessions={sessions} />;
}

/**
 * Checks that are their own bounded context rather than a configured LogType,
 * so they are always offered regardless of the nursery's daily log setup.
 */
const STANDALONE_CHECKS = [
  {
    title: "Fire Extinguisher Visual Checks",
    subtitle: "Record the monthly visual inspection of every extinguisher",
    to: "/fire-extinguisher-checks" as const,
    search: undefined,
  },
  ...EMERGENCY_LIGHT_CHECK_KINDS.map((kind) => ({
    title: EMERGENCY_LIGHT_CHECK_COPY[kind].title,
    subtitle: EMERGENCY_LIGHT_CHECK_COPY[kind].subtitle,
    to: "/emergency-light-checks" as const,
    search: { kind },
  })),
];

function LogSessionRows({ sessions }: { sessions: LogSession[] | undefined }) {
  return (
    <div className="flex w-full flex-col items-start gap-2">
      {STANDALONE_CHECKS.map((check) => (
        <Link
          key={check.title}
          to={check.to}
          search={check.search}
          className="w-full"
        >
          <div className="flex w-full items-center gap-3 rounded-lg border border-neutral-200 px-4 py-3 hover:bg-neutral-50 transition-colors cursor-pointer">
            <div className="flex grow flex-col items-start gap-0.5">
              <span className="text-body-bold font-body-bold text-default-font">
                {check.title}
              </span>
              <span className="text-caption font-caption text-subtext-color">
                {check.subtitle}
              </span>
            </div>
            <FeatherChevronRight className="h-4 w-4 text-subtext-color" />
          </div>
        </Link>
      ))}

      {sessions?.map((session) => (
        <Link
          key={session.logTypeId}
          to="/logs/$logTypeId"
          params={{ logTypeId: session.logTypeId }}
          className="w-full"
        >
          <div className="flex w-full items-center gap-3 rounded-lg border border-neutral-200 px-4 py-3 hover:bg-neutral-50 transition-colors cursor-pointer">
            <div className="flex grow flex-col items-start gap-0.5">
              <span className="text-body-bold font-body-bold text-default-font">
                {session.logTypeName}
              </span>
              {session.logTypeDescription && (
                <span className="text-caption font-caption text-subtext-color">
                  {session.logTypeDescription}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {session.entryCount > 0 && (
                <Badge variant="neutral">
                  {session.entryCount}{" "}
                  {session.entryCount === 1 ? "entry" : "entries"}
                </Badge>
              )}
              <FeatherChevronRight className="h-4 w-4 text-subtext-color" />
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}
